import React from 'react'
import DayPicker from 'react-day-picker'
import styles from './date-picker-styles.module.css'
import DateLogic from '../helpers/date-logic'
import DateRange from '../entities/date-range'
import { DateTime } from 'luxon'
import DateFormatter from '../../shared/helpers/date-format'
import { CalendarCaption } from '../../shared/components/calendar-caption'

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const INTERCEPTED_KEY_PRESSES = [
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight'
]

export default class DatePicker extends React.Component {
  pickerRef = React.createRef()

  state = {
    announcedMonth: this.props.dateRange.start,
    displayingCurrentMonth:
      this.getFirstSelectedDate().getMonth() + 1 === DateLogic.today.month
  }

  getSelectedDays() {
    return this.props.dateRange.toJSDateArray()
  }

  getFirstSelectedDate() {
    return this.getSelectedDays()[0]
  }

  getFirstDisabledDay() {
    return this.props.dateRange.isRange()
      ? DateRange.thisWeek.start
      : DateLogic.today
  }

  getTodayButtonLabel() {
    if (this.props.dateRange.isSingleDay()) {
      return 'Today'
    } else {
      return 'This Week'
    }
  }

  renderMonthAnnouncement() {
    let { announcedMonth } = this.state

    if (announcedMonth == null) {
      return null
    }

    return (
      <span
        className="sr-only"
        role="status"
      >
        {announcedMonth ? DateFormatter.monthYear(announcedMonth) : null}
      </span>
    )
  }

  render() {
    let { innerRef } = this.props
    let initialDate = this.getFirstSelectedDate()
    let selectedDays = this.getSelectedDays()
    let { displayingCurrentMonth } = this.state
    let firstDisabledDay = this.getFirstDisabledDay().toJSDate()

    return (
      <div
        className={styles.daypicker}
        ref={innerRef}
      >
        <DayPicker
          ref={this.pickerRef}
          showOutsideDays
          showWeekDays={false}
          classNames={styles}
          captionElement={CalendarCaption}
          fromMonth={new Date()}
          initialMonth={initialDate}
          selectedDays={selectedDays}
          weekdaysShort={SHORT_DAYS}
          onDayKeyDown={this.handleKeyDown}
          onKeyDown={this.handleKeyDown}
          onDayClick={this.handleDayClick}
          onCaptionClick={this.handleCaptionClick}
          onMonthChange={this.handleMonthChange}
          disabledDays={{
            before: firstDisabledDay
          }}
        />

        <div className={styles.mobileActions}>
          <button
            className={`button ${styles.todayButton}`}
            onClick={this.handleTodayClick}
            disabled={displayingCurrentMonth}
          >
            {this.getTodayButtonLabel()}
          </button>

          <button
            type="button"
            className={`button ${styles.closeButton}`}
            onClick={this.props.onDismiss}
          >
            Close
          </button>
        </div>

        {this.renderMonthAnnouncement()}
      </div>
    )
  }

  handleTodayClick = () => {
    this.showToday()
  }

  handleCaptionClick = () => {
    this.showToday()
  }

  // `react-day-picker` triggers navigation when these key presses
  // happen but does not prevent them from bubbling - we do
  // so that e.g. pressing `Down` doesn't both navigate to the
  // subsequent year _and_ scroll the page down.
  handleKeyDown = e => {
    if (INTERCEPTED_KEY_PRESSES.includes(e.key)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  closeAndNavigate = () => {
    let { navigate, fromTicketsApp } = this.props
    if (fromTicketsApp) {
      let [date, _] = DateLogic.getCurrentWeekStartEnd()
      navigate(DateFormatter.param(date))
    }
    this.props.onDismiss()
  }

  handleDayClick = (day, modifiers) => {
    let disabled = modifiers[styles.disabled]
    let selected = modifiers[styles.selected]

    if (disabled || selected) {
      return false
    }

    let { navigate, dateRange, fromTicketsApp } = this.props
    let date = DateTime.fromJSDate(day, { zone: 'America/Chicago' })

    if (dateRange.isSingleDay()) {
      navigate(`/calendar/day/${DateFormatter.param(date)}`)
    }
    if (fromTicketsApp) {
      // only the list view components from tickets app use this date picker
      // those components make their own calls and don't need the extra strings
      let [weekStart, _] = DateLogic.getWeekStartEnd(date)
      navigate(DateFormatter.param(weekStart))
    } else {
      let [weekStart, _] = DateLogic.getWeekStartEnd(date)
      navigate(`/calendar/week/${DateFormatter.param(weekStart)}`)
    }

    this.pickerRef.current.dayPicker.focus()
    this.props.onDismiss()
  }

  handleMonthChange = month => {
    const displayingCurrentMonth = DateTime.fromJSDate(month) <= DateLogic.today

    this.setState({
      displayingCurrentMonth: displayingCurrentMonth,
      announcedMonth: month
    })

    if (displayingCurrentMonth) {
      setTimeout(() => {
        this.pickerRef.current?.dayPicker.focus()
      }, 0)
    }
  }

  showToday() {
    let { navigate, dateRange, fromTicketsApp } = this.props

    // show the week/month of today when the user clicks "this week"
    this.pickerRef.current.showMonth(this.pickerRef.current.props.fromMonth)

    if (fromTicketsApp) {
      this.closeAndNavigate()
    } else if (dateRange.isSingleDay()) {
      let date = DateLogic.today
      navigate(`/calendar/day/${DateFormatter.param(date)}`)
    } else {
      let [date, _] = DateLogic.getCurrentWeekStartEnd()
      navigate(`/calendar/week/${DateFormatter.param(date)}`)
    }
  }
}
